/**
 * R232q53 — character customization mesh / texture swap pipeline.
 *
 * Wires the player's stored customization indexes (`skinVariant`,
 * `faceStyle`, `hairStyle`, `hairColourIdx`, `facialHair`) onto the
 * Sirus character rig that just loaded.
 *
 * Pipeline:
 *   1. Resolve textures via `/aiwari-api/dbc/char-section` (server endpoint
 *      returns the BLP→PNG URL — see aiwari-server `/dbc/char-section`).
 *   2. HEAD-probe each candidate URL to dodge SPA fallback HTML / 404.
 *   3. Apply the resolved texture to the matching rig sub-mesh:
 *        - skin   → body sub-meshes (`sub_<geosetId>` whose name is NOT
 *                   one of the Hair / Beard / Eye groups)
 *        - face   → face overlay (geoset group 1)
 *        - hair   → hair geoset group 0
 *        - facial-hair → geoset group 2 (200-299)
 *   4. Hair colour tints the hair material's diffuse colour (RGB tint on
 *      top of the swapped texture — WotLK 3.3.5a does this exact thing
 *      via SkinColours.dbc colour multipliers).
 *
 * **Concurrency cap = 3** so a creator slider that fires 5 picker changes
 * in a frame does not slam the server with 25 parallel BLP-PNG requests.
 *
 * **Silent fall-through on any 404** — customisation is purely cosmetic
 * overlay; if the server has no row for (race, gender, type, variant) the
 * rig keeps its baked diffuse and the player sees no error. We log
 * **once per missing tuple** at debug level only.
 *
 * AIWARI-BRAND: never log raw "CharSections" / "CharHairGeosets" strings
 * to the user-visible console. All log lines use the neutral phrase
 * "char appearance".
 */

import {
  type AbstractMesh,
  type Mesh,
  type Scene,
  type StandardMaterial,
  Color3,
  Texture,
} from '@babylonjs/core';

/** Customization indexes — match `CharacterDraft` in state/store.ts. */
export interface CharCustomization {
  skinVariant: number;
  faceStyle: number;
  hairStyle: number;
  hairColourIdx: number;
  facialHair: number;
}

/** Subset of `RealisticRig` we care about — only the meshes + the root. */
export interface CustomizableRig {
  meshes: AbstractMesh[];
}

/** Vanilla-ish hair tints (WotLK SkinColours.dbc range, 8 entries). */
const HAIR_COLOURS: Color3[] = [
  Color3.FromInts(48, 32, 22),    // 0 — black
  Color3.FromInts(96, 64, 48),    // 1 — dark brown
  Color3.FromInts(160, 110, 70),  // 2 — brown
  Color3.FromInts(220, 180, 100), // 3 — blond
  Color3.FromInts(245, 220, 160), // 4 — platinum
  Color3.FromInts(180, 60, 40),   // 5 — auburn
  Color3.FromInts(140, 150, 160), // 6 — silver
  Color3.FromInts(255, 255, 255), // 7 — white
];

const PLAYER_PORTAL_API = '/aiwari-api';

/** type alias accepted by /dbc/char-section. */
type SectionType = 'skin' | 'face' | 'hair' | 'facialHair';

/**
 * Limit concurrent fetch / texture-decode work to N at a time to avoid
 * 503-tripping the upstream when a slider scrubs across many variants.
 */
function makeConcurrencyGate(max: number): <T>(fn: () => Promise<T>) => Promise<T> {
  let active = 0;
  const queue: Array<() => void> = [];
  const next = (): void => {
    if (active >= max) return;
    const job = queue.shift();
    if (!job) return;
    active++;
    job();
  };
  return <T>(fn: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const run = (): void => {
        fn()
          .then(resolve, reject)
          .finally(() => {
            active--;
            next();
          });
      };
      queue.push(run);
      next();
    });
}

const gate = makeConcurrencyGate(3);

/** Cache HEAD-probe results so a slider scrub doesn't re-probe the same URL. */
const HEAD_CACHE = new Map<string, Promise<boolean>>();
const TEXTURE_URL_CACHE = new Map<string, Promise<string | null>>();

async function headProbe(url: string): Promise<boolean> {
  let p = HEAD_CACHE.get(url);
  if (p) return p;
  p = (async () => {
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok) return false;
      const ct = (res.headers.get('content-type') ?? '').toLowerCase();
      // Defeat SPA fallback HTML masquerading as 200.
      if (ct.includes('text/html')) return false;
      return true;
    } catch {
      return false;
    }
  })();
  HEAD_CACHE.set(url, p);
  return p;
}

interface SectionResolution {
  texture: string;
  variant: number;
  color: number;
}

async function resolveSection(
  race: string,
  gender: 'male' | 'female',
  type: SectionType,
  variant: number,
  color = 0,
): Promise<SectionResolution | null> {
  const cacheKey = `${race}|${gender}|${type}|${variant}|${color}`;
  let p = TEXTURE_URL_CACHE.get(cacheKey);
  if (p) {
    const url = await p;
    return url ? { texture: url, variant, color } : null;
  }
  p = (async (): Promise<string | null> => {
    const u = new URL(`${PLAYER_PORTAL_API}/dbc/char-section`, window.location.origin);
    u.searchParams.set('race', race);
    u.searchParams.set('gender', gender);
    u.searchParams.set('type', type);
    u.searchParams.set('variant', String(variant));
    u.searchParams.set('color', String(color));
    let res: Response;
    try {
      res = await fetch(u.toString());
    } catch {
      return null;
    }
    if (!res.ok) return null;
    const ct = (res.headers.get('content-type') ?? '').toLowerCase();
    if (!ct.includes('application/json')) return null;
    let body: { ok?: boolean; texture?: string };
    try {
      body = (await res.json()) as { ok?: boolean; texture?: string };
    } catch {
      return null;
    }
    if (!body.ok || !body.texture) return null;
    const ok = await headProbe(body.texture);
    return ok ? body.texture : null;
  })();
  TEXTURE_URL_CACHE.set(cacheKey, p);
  const url = await p;
  return url ? { texture: url, variant, color } : null;
}

/**
 * Heuristic: given a sub-mesh name, classify it into one of the three
 * customisable groups. Sirus M2→GLB exporter names every body sub-mesh
 * `sub_<geosetID>_lvl_<n>` — the geoset id's hundreds digit encodes the
 * group (0xx hair, 1xx face, 2xx facial-hair, 3xx ears, 4xx wrist, ...).
 *
 * Falls back to substring matching for non-Sirus rigs (placeholder builds,
 * KayKit fallback rigs) so the swap still works on smoke-test characters.
 */
type MeshGroup = 'body' | 'face' | 'hair' | 'facialHair' | 'other';

function classifyMesh(name: string): MeshGroup {
  const lc = name.toLowerCase();
  const m = /^sub_(\d+)_lvl_/i.exec(name);
  if (m) {
    const id = parseInt(m[1]!, 10);
    if (Number.isFinite(id)) {
      const group = Math.floor(id / 100);
      if (group === 0)  return 'hair';
      if (group === 1)  return 'face';
      if (group === 2)  return 'facialHair';
      // Geoset 3 (ears) follows skin colour; treat as "body".
      if (group === 3)  return 'body';
      return 'other';
    }
  }
  if (lc.includes('hair') && !lc.includes('beard') && !lc.includes('moustache') && !lc.includes('mustach')) return 'hair';
  if (lc.includes('beard') || lc.includes('moustache') || lc.includes('mustach') || lc.includes('facialhair') || lc.includes('facial_hair')) return 'facialHair';
  if (lc.includes('face') || lc.includes('head')) return 'face';
  if (lc.includes('eye')) return 'other';
  return 'body';
}

/**
 * Apply a fetched texture URL to every mesh classified into `group`.
 * Falls back silently if the rig has no mesh in that group (e.g. female
 * rigs have no facial-hair geosets).
 */
function applyTextureToGroup(
  scene: Scene,
  rig: CustomizableRig,
  group: MeshGroup,
  url: string,
  tint?: Color3,
): void {
  let applied = 0;
  for (const m of rig.meshes) {
    if (classifyMesh(m.name) !== group) continue;
    const mesh = m as Mesh;
    const mat = mesh.material as StandardMaterial | null;
    if (!mat) continue;
    // Only StandardMaterial has diffuseTexture; PBRMaterial uses albedoTexture.
    // Sirus M2→GLB extractor emits StandardMaterial in 99% of cases.
    const tex = new Texture(
      url, scene,
      true,        // noMipmap=false (=invert Y)
      false,       // invertY
      Texture.TRILINEAR_SAMPLINGMODE,
    );
    tex.hasAlpha = false;
    // Dispose old texture so we don't leak GPU memory on slider scrub.
    const matAny = mat as unknown as { diffuseTexture?: { dispose?: () => void } | null; diffuseColor?: Color3 };
    const prev = matAny.diffuseTexture;
    if (prev && typeof prev.dispose === 'function') {
      try { prev.dispose(); } catch { /* nop */ }
    }
    matAny.diffuseTexture = tex;
    if (tint) {
      matAny.diffuseColor = tint.clone();
    }
    applied++;
  }
  if (applied === 0) {
    // Don't spam the console on every slider tick — log once at debug.
    console.debug(`[char appearance] no mesh in group "${group}" — skip swap.`);
  }
}

/**
 * Apply the full set of customisation values to a freshly-loaded rig.
 * The function is **idempotent** — call it again with new values to
 * re-skin the same rig.
 *
 * Errors are swallowed (logged at debug only) so a missing texture never
 * breaks the rig load. Caller sees the function resolve once every
 * pending fetch settles.
 */
export async function applyCustomization(
  scene: Scene,
  rig: CustomizableRig,
  race: string,
  gender: 'male' | 'female',
  custom: CharCustomization,
): Promise<void> {
  const tasks: Array<Promise<void>> = [];
  // Hair colour — used both as a tint on the hair geoset and as the
  // `color` argument when fetching the texture itself (WotLK 3.3.5a
  // CharSections.dbc encodes hair colour as the second axis of the row).
  const hairTint = HAIR_COLOURS[custom.hairColourIdx % HAIR_COLOURS.length];

  tasks.push(
    gate(() => resolveSection(race, gender, 'skin', custom.skinVariant, custom.skinVariant))
      .then((r) => { if (r) applyTextureToGroup(scene, rig, 'body', r.texture); })
      .catch(() => { /* silent fall-through */ }),
  );
  tasks.push(
    gate(() => resolveSection(race, gender, 'face', custom.faceStyle, custom.skinVariant))
      .then((r) => { if (r) applyTextureToGroup(scene, rig, 'face', r.texture); })
      .catch(() => { /* silent fall-through */ }),
  );
  tasks.push(
    gate(() => resolveSection(race, gender, 'hair', custom.hairStyle, custom.hairColourIdx))
      .then((r) => { if (r) applyTextureToGroup(scene, rig, 'hair', r.texture, hairTint); })
      .catch(() => { /* silent fall-through */ }),
  );
  if (gender === 'male' && custom.facialHair > 0) {
    tasks.push(
      gate(() => resolveSection(race, gender, 'facialHair', custom.facialHair, custom.hairColourIdx))
        .then((r) => { if (r) applyTextureToGroup(scene, rig, 'facialHair', r.texture, hairTint); })
        .catch(() => { /* silent fall-through */ }),
    );
  }
  await Promise.allSettled(tasks);
}

// ── R232q65: armor texture-overlay pipeline ──────────────────────────

/**
 * AIWARI-side armor slot keys that participate in TEXTURE OVERLAY (NOT
 * mesh-attach). Sirus has no separate M2 mesh for any of these — they
 * rendered in WotLK 3.3.5a via geoset visibility + a swap of the body
 * diffuse texture in the matching region.
 *
 * Slots that DO have separate meshes (head, shoulders, cape, mainhand,
 * offhand) are NOT in this list — they go through `attachWeaponToBone`.
 */
export type ArmorOverlaySlot = 'chest' | 'legs' | 'feet' | 'hands' | 'wrists' | 'bracer';

/** Mesh group on the body rig that the slot's texture should land on.
 *  These match the geoset hundreds-digit classification used elsewhere
 *  in this file — we extend `MeshGroup` with armor-specific buckets so
 *  the swap targets the right sub-meshes without touching skin/face.
 *
 *  All armor groups land on the body (geoset 0xx in Sirus naming) but
 *  we differentiate sub-regions by the lvl_<n> suffix when present —
 *  WotLK 3.3.5a M2 splits the body mesh into TorsoUpper / TorsoLower /
 *  ArmUpper / ArmLower / Hand / Leg / Foot via `lvl_` tiers. The
 *  helper below picks the meshes whose `lvl_<n>` falls inside the
 *  range that the requested slot covers. */
const ARMOR_SLOT_LVL_RANGES: Record<ArmorOverlaySlot, [number, number]> = {
  // Conservative starting ranges — refined when telemetry from real rigs
  // arrives. All sub-meshes within the [min, max] lvl bucket get the
  // overlay; out-of-range meshes stay on the skin texture.
  chest:  [0, 1],   // TorsoUpper + TorsoLower
  legs:   [2, 3],   // LegUpper + LegLower
  feet:   [4, 4],   // Foot
  hands:  [5, 5],   // Hand
  wrists: [6, 6],   // ArmUpper (bracer overlay region)
  bracer: [7, 7],   // ArmLower
};

interface ItemTextureResponse {
  ok?: boolean;
  primary?: string | null;
  secondary?: string | null;
  reason?: string;
}

/** Cache the resolved URL per (slot, itemId) so re-equipping the same
 *  item doesn't refetch. Also caches NULL so a known-miss doesn't
 *  re-probe on every equipment-change tick. */
const ITEM_TEXTURE_CACHE = new Map<string, Promise<{ primary: string | null; secondary: string | null } | null>>();

async function fetchItemTexture(
  slot: ArmorOverlaySlot,
  itemId: string,
): Promise<{ primary: string | null; secondary: string | null } | null> {
  const cacheKey = `${slot}|${itemId}`;
  let p = ITEM_TEXTURE_CACHE.get(cacheKey);
  if (p) return p;
  p = (async (): Promise<{ primary: string | null; secondary: string | null } | null> => {
    const u = new URL(
      `${PLAYER_PORTAL_API}/dbc/item-texture/${encodeURIComponent(slot)}/${encodeURIComponent(itemId)}`,
      window.location.origin,
    );
    let res: Response;
    try {
      res = await fetch(u.toString());
    } catch {
      return null;
    }
    if (!res.ok) return null;
    const ct = (res.headers.get('content-type') ?? '').toLowerCase();
    if (!ct.includes('application/json')) return null;
    let body: ItemTextureResponse;
    try {
      body = (await res.json()) as ItemTextureResponse;
    } catch {
      return null;
    }
    if (!body.ok) return null;
    const primary = body.primary ?? null;
    const secondary = body.secondary ?? null;
    if (!primary && !secondary) return null;
    // HEAD-probe both URLs; if neither resolves, the BLP for this item
    // hasn't been extracted yet — silent fall-through, body stays as is.
    const [primaryOk, secondaryOk] = await Promise.all([
      primary ? headProbe(primary) : Promise.resolve(false),
      secondary ? headProbe(secondary) : Promise.resolve(false),
    ]);
    const out = {
      primary: primaryOk ? primary : null,
      secondary: secondaryOk ? secondary : null,
    };
    if (!out.primary && !out.secondary) return null;
    return out;
  })();
  ITEM_TEXTURE_CACHE.set(cacheKey, p);
  return p;
}

/** Pick the body sub-meshes that fall inside the given lvl_<n> range so
 *  we only overlay the region the equipped item actually covers. Falls
 *  back to ALL body meshes when the rig doesn't use the `sub_<id>_lvl_<n>`
 *  Sirus naming (placeholder / KayKit fallback rigs render the whole body
 *  as one mesh — overlaying the whole thing is the cheapest correct
 *  behaviour for those). */
function pickArmorMeshes(
  rig: CustomizableRig,
  slot: ArmorOverlaySlot,
): AbstractMesh[] {
  const [lvlMin, lvlMax] = ARMOR_SLOT_LVL_RANGES[slot];
  const out: AbstractMesh[] = [];
  let sawSirusNaming = false;
  for (const m of rig.meshes) {
    if (classifyMesh(m.name) !== 'body') continue;
    const lvl = /sub_\d+_lvl_(\d+)/i.exec(m.name);
    if (lvl) {
      sawSirusNaming = true;
      const n = Number.parseInt(lvl[1]!, 10);
      if (Number.isFinite(n) && n >= lvlMin && n <= lvlMax) out.push(m);
    }
  }
  if (out.length > 0) return out;
  // Fallback: rig isn't Sirus-named — overlay the whole body.
  if (!sawSirusNaming) {
    for (const m of rig.meshes) {
      if (classifyMesh(m.name) === 'body') out.push(m);
    }
  }
  return out;
}

function applyArmorTextureToMeshes(
  scene: Scene,
  meshes: AbstractMesh[],
  url: string,
): void {
  if (meshes.length === 0) return;
  const tex = new Texture(
    url, scene,
    true,    // noMipmap
    false,   // invertY
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  tex.hasAlpha = false;
  for (const m of meshes) {
    const mesh = m as Mesh;
    const mat = mesh.material as StandardMaterial | null;
    if (!mat) continue;
    const matAny = mat as unknown as { diffuseTexture?: { dispose?: () => void } | null };
    const prev = matAny.diffuseTexture;
    if (prev && typeof prev.dispose === 'function') {
      try { prev.dispose(); } catch { /* nop */ }
    }
    matAny.diffuseTexture = tex;
  }
}

/**
 * Apply armor texture overlays for the equipped item on the given slot.
 * Silent fall-through on:
 *   - server returns `ok: false` (unknown item / no display row / non-armor),
 *   - HEAD-probe fails on every candidate URL (BLP not extracted yet),
 *   - rig has no body mesh in the slot's lvl range (rig is mesh-attach
 *     style — caller already handled it via attachWeaponToBone).
 *
 * Idempotent — re-call with a different itemId to re-overlay; pass an
 * empty / null itemId to skip the slot. **Does NOT touch weapon slots
 * (mainhand/offhand) or mesh-attach slots (head/shoulders/cape).**
 */
export async function applyArmorOverlay(
  scene: Scene,
  rig: CustomizableRig,
  slot: ArmorOverlaySlot,
  itemId: string | null | undefined,
): Promise<void> {
  if (!itemId) return;
  try {
    const urls = await gate(() => fetchItemTexture(slot, itemId));
    if (!urls) return;
    const meshes = pickArmorMeshes(rig, slot);
    if (meshes.length === 0) return;
    if (urls.primary) {
      applyArmorTextureToMeshes(scene, meshes, urls.primary);
    }
    if (urls.secondary && slot === 'chest') {
      // Chest items overlay an additional band on TorsoLower (lvl_1) —
      // pick that sub-range explicitly and apply secondary.
      const lower: AbstractMesh[] = [];
      for (const m of rig.meshes) {
        if (classifyMesh(m.name) !== 'body') continue;
        const lvl = /sub_\d+_lvl_(\d+)/i.exec(m.name);
        if (lvl && Number.parseInt(lvl[1]!, 10) === 1) lower.push(m);
      }
      if (lower.length > 0) {
        applyArmorTextureToMeshes(scene, lower, urls.secondary);
      }
    }
    if (urls.secondary && slot === 'legs') {
      const lower: AbstractMesh[] = [];
      for (const m of rig.meshes) {
        if (classifyMesh(m.name) !== 'body') continue;
        const lvl = /sub_\d+_lvl_(\d+)/i.exec(m.name);
        if (lvl && Number.parseInt(lvl[1]!, 10) === 3) lower.push(m);
      }
      if (lower.length > 0) {
        applyArmorTextureToMeshes(scene, lower, urls.secondary);
      }
    }
  } catch {
    /* silent fall-through — armor overlay must never break the rig */
  }
}

/** Bulk variant: apply overlays for every overlay-slot in `equipment`
 *  in parallel (gated to MAX 3 concurrent fetches). Silently skips
 *  slots whose itemId is null/empty or unknown. */
export async function applyEquipmentArmorOverlays(
  scene: Scene,
  rig: CustomizableRig,
  equipment: Partial<Record<ArmorOverlaySlot, string | null | undefined>>,
): Promise<void> {
  const slots: ArmorOverlaySlot[] = ['chest', 'legs', 'feet', 'hands', 'wrists', 'bracer'];
  const tasks: Array<Promise<void>> = [];
  for (const s of slots) {
    const id = equipment[s];
    if (!id) continue;
    tasks.push(applyArmorOverlay(scene, rig, s, id));
  }
  await Promise.allSettled(tasks);
}

/** Test-only: clear the in-memory caches so unit tests don't bleed state. */
export function _resetCharCustomizationCachesForTest(): void {
  HEAD_CACHE.clear();
  TEXTURE_URL_CACHE.clear();
  ITEM_TEXTURE_CACHE.clear();
}
